import { Head } from "vite-react-ssg";

const BASE_URL = "https://www.hollinsandhur.com";

/**
 * Sitewide JSON-LD structured data rendered once in the global Layout.
 * 
 * @id strategy:
 *   - Organization:      https://www.hollinsandhur.com/#organization
 *   - WebSite:           https://www.hollinsandhur.com/#website
 *   - AccountingService: https://www.hollinsandhur.com/#localbusiness
 *
 * All NAP details sourced from visible site content (footer).
 * No aggregateRating (only pages showing reviews should carry that).
 * No openingHours (not displayed on the website).
 * No priceRange (not stated on the website).
 * No SearchAction (no on-site search exists).
 */
export const GlobalStructuredData = () => {
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": `${BASE_URL}/#organization`,
    "name": "Hollins & Hur",
    "url": BASE_URL,
    "logo": {
      "@type": "ImageObject",
      "url": `${BASE_URL}/lovable-uploads/168b6775-5871-449b-a0c6-d41641359e23.png`,
      "width": 250,
      "height": 125
    },
    "description": "Award-winning small business accounting and tax firm in Manassas, Virginia serving Northern Virginia and Washington DC.",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "10550 Linden Lake, Suite 300",
      "addressLocality": "Manassas",
      "addressRegion": "VA",
      "postalCode": "20109",
      "addressCountry": "US"
    },
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+1-571-222-4765",
      "email": "sales@hollinsandhur.com",
      "contactType": "Customer Service",
      "areaServed": "US",
      "availableLanguage": "English"
    },
    "sameAs": [
      "https://www.facebook.com/hollinsandhur",
      "https://www.linkedin.com/company/hollins-and-hur"
    ]
  };

  const webSiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${BASE_URL}/#website`,
    "name": "Hollins & Hur",
    "url": BASE_URL,
    "publisher": {
      "@id": `${BASE_URL}/#organization`
    }
  };

  const accountingServiceSchema = {
    "@context": "https://schema.org",
    "@type": "AccountingService",
    "@id": `${BASE_URL}/#localbusiness`,
    "name": "Hollins & Hur",
    "url": BASE_URL,
    "logo": `${BASE_URL}/lovable-uploads/168b6775-5871-449b-a0c6-d41641359e23.png`,
    "image": `${BASE_URL}/og-image-logo.jpg`,
    "description": "Hollins & Hur is a small business accounting firm based in Manassas, Virginia. We provide bookkeeping, tax preparation, tax planning, and advisory support to established small businesses across Northern Virginia and Washington DC.",
    "telephone": "+1-571-222-4765",
    "email": "sales@hollinsandhur.com",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "10550 Linden Lake, Suite 300",
      "addressLocality": "Manassas",
      "addressRegion": "VA",
      "postalCode": "20109",
      "addressCountry": "US"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 38.783,
      "longitude": -77.523
    },
    "areaServed": [
      { "@type": "City", "name": "Manassas" },
      { "@type": "City", "name": "Gainesville" },
      { "@type": "City", "name": "Haymarket" },
      { "@type": "AdministrativeArea", "name": "Northern Virginia" },
      { "@type": "City", "name": "Washington", "alternateName": "Washington DC" }
    ],
    "parentOrganization": {
      "@id": `${BASE_URL}/#organization`
    },
    "serviceType": [
      "Tax Preparation",
      "Tax Planning",
      "Bookkeeping",
      "Business Advisory",
      "Small Business Accounting"
    ]
  };

  return (
    <Head>
      <script type="application/ld+json">
        {JSON.stringify(organizationSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(webSiteSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(accountingServiceSchema)}
      </script>
    </Head>
  );
};
